import {
  ApiWebhookEvent,
  IBrand,
  IOrganisation,
  IPractice,
  IWebhook,
  IWebhookEvent,
  OrganisationCollection,
  PartnerAppCollection,
  RootCollection,
  WebhookCollection,
  WebhookStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  asReffable,
  asyncForAll,
  AtLeast,
  collectionGroupQuery,
  CollectionReference,
  DocumentReference,
  Firestore,
  IReffable,
  isSameRef,
  subCollection,
  where,
  WithRef,
} from '@principle-theorem/shared';
import { Brand } from '../brand';
import { Practice } from '../practice/practice';

export const MAX_CONSECUTIVE_WEBHOOK_FAILURES = 10;

export class Webhook {
  static init(
    overrides: AtLeast<
      IWebhook,
      | 'label'
      | 'endpointUrl'
      | 'secret'
      | 'events'
      | 'userRef'
      | 'organisationRef'
      | 'allowedPractices'
    >
  ): IWebhook {
    return {
      deleted: false,
      status: WebhookStatus.Enabled,
      consecutiveFailures: 0,
      ...overrides,
    };
  }

  static eventCol(
    webhook: IReffable<IWebhook>
  ): CollectionReference<IWebhookEvent> {
    return subCollection<IWebhookEvent>(webhook, WebhookCollection.Events);
  }

  static async getActiveWebhooksForPractice(
    practiceRef: DocumentReference<IPractice>,
    event: ApiWebhookEvent
  ): Promise<WithRef<IWebhook>[]> {
    const brandRef = Practice.brandRef(asReffable(practiceRef));
    const organisationRef = Brand.organisationRef(asReffable(brandRef));

    const webhooks = await Webhook.getActiveWebhooksForOrganisation(
      organisationRef,
      event
    );

    return webhooks.filter((webhook) =>
      webhook.allowedPractices.some((allowedPractice) =>
        isSameRef(allowedPractice, practiceRef)
      )
    );
  }

  static async getActiveWebhooksForOrganisation(
    organisationRef: DocumentReference<IOrganisation>,
    event: ApiWebhookEvent
  ): Promise<WithRef<IWebhook>[]> {
    const partnerWebhooks = await Firestore.getDocs(
      collectionGroupQuery<IWebhook>(
        PartnerAppCollection.Webhooks,
        where('organisationRef', '==', organisationRef),
        where('status', '==', WebhookStatus.Enabled),
        where('events', 'array-contains', event),
        where('deleted', '==', false)
      )
    );

    const organisationWebhooks = await Firestore.getDocs(
      collectionGroupQuery<IWebhook>(
        OrganisationCollection.Webhooks,
        where('organisationRef', '==', organisationRef),
        where('status', '==', WebhookStatus.Enabled),
        where('events', 'array-contains', event),
        where('deleted', '==', false)
      )
    );

    return [...partnerWebhooks, ...organisationWebhooks];
  }

  static async getActiveWebhooksForBrand(
    brandRef: DocumentReference<IBrand>,
    event: ApiWebhookEvent
  ): Promise<WithRef<IWebhook>[]> {
    const organisationRef = Brand.organisationRef(asReffable(brandRef));

    const brandWebhooks = await Webhook.getActiveWebhooksForOrganisation(
      organisationRef,
      event
    );

    const practices = await Firestore.getDocs(
      Brand.practiceCol({ ref: brandRef })
    );
    const practiceRefs = practices.map((practice) => practice.ref);

    return brandWebhooks.filter(
      (brandWebhook) =>
        brandWebhook.allowedPractices.length &&
        brandWebhook.allowedPractices.some(
          (allowedPractice) =>
            practiceRefs.length &&
            practiceRefs.some((practiceRef) =>
              isSameRef(allowedPractice, practiceRef)
            )
        )
    );
  }

  static async addEventsForWebhook(
    webhooks: IReffable<IWebhook>[],
    event: IWebhookEvent
  ): Promise<void> {
    await asyncForAll(webhooks, (webhook) =>
      Firestore.addDoc(Webhook.eventCol(webhook), event)
    );
  }

  static isPartnerAppWebhook(event: IReffable<IWebhook>): boolean {
    return event.ref.path.startsWith(`${RootCollection.Partners}/`);
  }

  static isOrganisationWebhook(event: IReffable<IWebhook>): boolean {
    return event.ref.path.startsWith(`${RootCollection.Organisations}/`);
  }
}
